import type { CoverageCell } from "@components/campaigns/CoverageMatrix";
import {
  type DimensionOption,
  StepAssessmentPlanFields,
} from "@components/campaigns/StepAssessmentPlanFields";
import {
  StepParticipantsEditor,
  type TeamMember,
} from "@components/campaigns/StepParticipantsEditor";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  isInterviewFlavoured,
  type SelectionProcessStep,
  STEP_TYPE_LABELS,
} from "@lib/selectionSteps";
import { ChevronDown, ChevronRight, GripVertical, Minus } from "lucide-react";

const STEP_TYPES = [
  { key: "recruiter_call", label: "Recruiter call" },
  { key: "recruiter_interview", label: "Recruiter interview" },
  { key: "interview", label: "Interview" },
  { key: "psychometric", label: "Psychometric" },
  { key: "final", label: "Final" },
] as const;

const STEP_FORMATS = [
  { key: "phone", label: "Phone" },
  { key: "video", label: "Video call" },
  { key: "in_person", label: "In person" },
  { key: "async_video", label: "Async video" },
  { key: "take_home", label: "Take-home" },
  { key: "live_coding", label: "Live coding" },
  { key: "psychometric_test", label: "Psychometric test" },
  { key: "reference_check", label: "Reference check" },
] as const;

const DEPTH_LABEL: Record<string, string> = {
  sample: "Screen",
  probe: "Probe",
  deep_dive: "Deep dive",
};

interface StepCardProps {
  // The dnd-kit sortable identifier — the editor's stable _key for this row.
  id: string;
  step: SelectionProcessStep;
  // 0-based position for the "Step N" header (N = index + 1).
  index: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onChange: (next: SelectionProcessStep) => void;
  onRemove: () => void;
  locked: boolean;
  teamMembers: TeamMember[];
  // Coverage matrix inputs for the read-only summary used on non-interview
  // steps. Editing happens inside StepAssessmentPlanFields for interview steps.
  dimensions: DimensionOption[];
  coverageCells: CoverageCell[];
  // Inline error message shown beneath the card body, set by the parent when
  // the per-step plan mutation failed during the batched save-on-confirm.
  saveError?: string | null;
  // True when the assessment matrix's bands no longer line up with the
  // current blueprint dimensions. Forwarded to StepAssessmentPlanFields so
  // its AI suggest CTAs can disable themselves cleanly.
  matrixOutOfSync?: boolean;
  // Forwarded to StepAssessmentPlanFields so its AI suggest / acknowledge can
  // tell the parent to refresh after their dedicated mutations land.
  onPlanSaved: () => void;
}

export function StepCard({
  id,
  step,
  index,
  expanded,
  onToggleExpanded,
  onChange,
  onRemove,
  locked: _locked,
  teamMembers,
  dimensions,
  coverageCells,
  saveError,
  matrixOutOfSync = false,
  onPlanSaved,
}: StepCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const interviewFlavoured = isInterviewFlavoured(step.stepType);
  const stepTypeLabel = STEP_TYPE_LABELS[step.stepType] ?? step.stepType;
  const idPrefix = `step-${id}`;

  const updateStep = (patch: Partial<SelectionProcessStep>) => {
    onChange({ ...step, ...patch });
  };

  const updatePlan = (next: {
    intent: string | null;
    coverage: { dimensionId: string; depth: string }[];
  }) => {
    onChange({
      ...step,
      assessmentPlan: {
        intent: next.intent,
        coverage: next.coverage,
        versionNumber: step.assessmentPlan?.versionNumber ?? null,
        versionOrigin: step.assessmentPlan?.versionOrigin ?? null,
        acknowledgedAt: step.assessmentPlan?.acknowledgedAt ?? null,
      },
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`step-card-${id}`}
      className="rounded-lg border border-border bg-card shadow-sm"
    >
      <div className="flex items-center gap-2 p-2">
        <button
          type="button"
          aria-label={`Reorder ${stepTypeLabel} step`}
          data-testid="step-card-drag-handle"
          className="flex-shrink-0 cursor-grab touch-none rounded p-0.5 text-text-faint hover:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-label={expanded ? "Collapse step details" : "Expand step details"}
          aria-expanded={expanded}
          className="rounded p-0.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className="rounded bg-card-hover px-1.5 py-0.5 text-xs font-medium text-text-secondary">
          Step {index + 1}
        </span>
        <span className="rounded bg-card-hover px-1.5 py-0.5 text-xs text-text-faint">
          {stepTypeLabel}
        </span>
        <input
          type="text"
          value={step.label ?? ""}
          onChange={(e) => updateStep({ label: e.target.value })}
          aria-label="Label"
          placeholder="Label (e.g. 1st interview)"
          className="flex-1 rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove step"
          className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
        >
          <Minus size={14} />
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-border bg-background/20 p-3">
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              Structure
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor={`${idPrefix}-step-type`}
                  className="mb-1 block text-xs font-medium text-text-secondary"
                >
                  Step type
                </label>
                <select
                  id={`${idPrefix}-step-type`}
                  value={step.stepType}
                  onChange={(e) => updateStep({ stepType: e.target.value })}
                  aria-label="Step type"
                  className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  {STEP_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-format`}
                  className="mb-1 block text-xs font-medium text-text-secondary"
                >
                  Format
                </label>
                <select
                  id={`${idPrefix}-format`}
                  value={step.stepFormat ?? ""}
                  onChange={(e) =>
                    updateStep({ stepFormat: e.target.value === "" ? null : e.target.value })
                  }
                  aria-label="Format"
                  className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">— Select format —</option>
                  {STEP_FORMATS.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-duration`}
                  className="mb-1 block text-xs font-medium text-text-secondary"
                >
                  Duration (minutes)
                </label>
                <input
                  id={`${idPrefix}-duration`}
                  type="number"
                  min={1}
                  value={step.durationMinutes ?? ""}
                  onChange={(e) =>
                    updateStep({
                      durationMinutes:
                        e.target.value === "" ? null : Number.parseInt(e.target.value, 10),
                    })
                  }
                  aria-label="Duration in minutes"
                  placeholder="60"
                  className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium text-text-secondary">
                Participants
              </span>
              <StepParticipantsEditor
                value={step.participants ?? []}
                onChange={(next) => updateStep({ participants: next })}
                teamMembers={teamMembers}
                idPrefix={idPrefix}
              />
            </div>
            <div>
              <label
                htmlFor={`${idPrefix}-purpose`}
                className="mb-1 block text-xs font-medium text-text-secondary"
              >
                Purpose
              </label>
              <textarea
                id={`${idPrefix}-purpose`}
                value={step.purpose ?? ""}
                onChange={(e) =>
                  updateStep({ purpose: e.target.value === "" ? null : e.target.value })
                }
                aria-label="Step purpose"
                placeholder="Why does this step exist in the process?"
                rows={2}
                className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </section>

          {interviewFlavoured ? (
            <section
              className="border-t border-border pt-4"
              data-testid={`step-card-${id}-assessment-plan`}
            >
              <StepAssessmentPlanFields
                selectionStepId={step.id ?? id}
                value={
                  step.assessmentPlan ?? {
                    intent: null,
                    coverage: [],
                    versionNumber: null,
                    versionOrigin: null,
                    acknowledgedAt: null,
                  }
                }
                onChange={updatePlan}
                dimensions={dimensions}
                matrixOutOfSync={matrixOutOfSync}
                onSaved={onPlanSaved}
              />
            </section>
          ) : (
            <section
              className="border-t border-border pt-4"
              data-testid={`step-card-${id}-coverage-summary`}
            >
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Coverage
              </h4>
              <ReadOnlyCoverageRow
                stepContinuityId={step.continuityId ?? ""}
                dimensions={dimensions}
                cells={coverageCells}
              />
            </section>
          )}
        </div>
      )}

      {saveError && (
        <div
          data-testid={`step-card-${id}-error`}
          className="border-t border-border bg-red-50 px-3 py-2 text-xs text-red-800"
        >
          {saveError}
        </div>
      )}
    </div>
  );
}

interface ReadOnlyCoverageRowProps {
  stepContinuityId: string;
  dimensions: DimensionOption[];
  cells: CoverageCell[];
}

function ReadOnlyCoverageRow({ stepContinuityId, dimensions, cells }: ReadOnlyCoverageRowProps) {
  if (dimensions.length === 0) {
    return (
      <p className="text-xs text-text-faint">
        Coverage is derived from interview-flavoured steps' assessment plans.
      </p>
    );
  }
  const cellByDim = new Map<string, CoverageCell>();
  for (const c of cells) {
    if (c.stepContinuityId === stepContinuityId) {
      cellByDim.set(c.dimensionContinuityId, c);
    }
  }
  return (
    <ul className="flex flex-wrap gap-2 text-xs">
      {dimensions.map((d) => {
        const cell = cellByDim.get(d.continuityId);
        const depth = cell?.depth ?? null;
        return (
          <li key={d.continuityId} className="flex items-center gap-1">
            <span className="text-text-secondary">{d.name}:</span>
            <span className="text-text-faint">{depth ? DEPTH_LABEL[depth] : "—"}</span>
          </li>
        );
      })}
    </ul>
  );
}
